import type { ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'
import {
  Ban,
  Check,
  CheckCheck,
  CloudUpload,
  Hourglass,
  RotateCcw,
  Sparkles,
  SquareCheck
} from 'lucide-react'
import { Button, Tooltip } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatNumber, pluralize } from '@/lib/format'

/**
 * The one action currently in flight. `'fetch'` belongs to the page header — the
 * bar only uses it to disable itself while a fetch is being started.
 */
export type MediaAction =
  | 'fetch'
  | 'generate-page'
  | 'generate-all'
  | 'cancel'
  | 'retry-failed'
  | 'approve-selected'
  | 'approve-all'
  | 'save'

export interface BatchActionBarProps {
  /** Human label of the active filter, for the "everything matching" copy. */
  filterLabel: string
  /** Rows matching the active filter server-side. */
  matchingTotal: number
  /** Images on this page that have no generated alt text yet. */
  pageEligible: number
  failedOnPage: number
  selectedCount: number
  pageCount: number
  /** Approved but not yet written to WordPress. */
  needsSaveCount: number
  jobActive: boolean
  /**
   * The active job is QUEUED, not RUNNING. Optional so the page can omit it, but
   * without it the bar has to hedge: a batch waiting for a generation slot has not
   * started, and no control here may suggest otherwise.
   */
  jobQueued?: boolean
  pending: MediaAction | null
  onGeneratePage: () => void
  onGenerateAll: () => void
  onCancel: () => void
  onRetryFailed: () => void
  onSelectAll: () => void
  onClearSelection: () => void
  onApproveSelected: () => void
  onApproveAll: () => void
  onSave: () => void
}

export function BatchActionBar({
  filterLabel,
  matchingTotal,
  pageEligible,
  failedOnPage,
  selectedCount,
  pageCount,
  needsSaveCount,
  jobActive,
  jobQueued = false,
  pending,
  onGeneratePage,
  onGenerateAll,
  onCancel,
  onRetryFailed,
  onSelectAll,
  onClearSelection,
  onApproveSelected,
  onApproveAll,
  onSave
}: BatchActionBarProps): ReactElement {
  const [stuck, setStuck] = useState(false)
  const sentinel = useRef<HTMLDivElement>(null)

  // Long lists are the norm here, so the bar follows the scroll. The sentinel
  // above it is what tells us to lift it off the page with a shadow.
  useEffect(() => {
    const node = sentinel.current
    if (!node || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (entry) setStuck(!entry.isIntersecting)
      },
      { threshold: 1 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  const busy = pending !== null
  const canGeneratePage = !jobActive && !busy && pageEligible > 0
  const canGenerateAll = !jobActive && !busy && matchingTotal > 0

  // A batch this site is already waiting on blocks a second one just as a running
  // batch does, but "already running" would be a lie while it sits in the queue.
  const inFlightNote = jobQueued
    ? 'This site already has a batch waiting for a generation slot — nothing has started yet'
    : 'This site already has a batch running'

  return (
    <>
      <div ref={sentinel} aria-hidden="true" className="h-px w-full" />

      <div
        className={cn(
          'sticky top-0 z-20 rounded-card border border-line bg-surface/90 p-3 backdrop-blur-[2px] transition-shadow duration-150 ease-out-quart',
          stuck ? 'shadow-pop' : 'shadow-sheet'
        )}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Tooltip
            content={
              jobActive
                ? inFlightNote
                : pageEligible > 0
                  ? `Generate alt text for the ${formatNumber(pageEligible)} ${pluralize(pageEligible, 'image')} on this page that has none`
                  : 'Every image on this page already has alt text — use Regenerate on a card'
            }
          >
            <Button
              size="sm"
              iconLeft={<Sparkles />}
              onClick={onGeneratePage}
              loading={pending === 'generate-page'}
              disabled={!canGeneratePage}
            >
              Generate page
              {pageEligible > 0 ? ` (${formatNumber(pageEligible)})` : ''}
            </Button>
          </Tooltip>

          <Tooltip
            content={
              jobActive
                ? inFlightNote
                : `Everything matching “${filterLabel}” — ${formatNumber(matchingTotal)} ${pluralize(matchingTotal, 'image')}`
            }
          >
            <Button
              size="sm"
              variant="secondary"
              iconLeft={<Sparkles />}
              onClick={onGenerateAll}
              loading={pending === 'generate-all'}
              disabled={!canGenerateAll}
            >
              Generate all
            </Button>
          </Tooltip>

          {jobActive ? (
            <Tooltip
              content={
                jobQueued
                  ? 'Drop this batch out of the queue. Nothing has been generated yet, so nothing is lost'
                  : 'Stop after the image currently in flight'
              }
            >
              <Button
                size="sm"
                variant="danger"
                iconLeft={jobQueued ? <Hourglass /> : <Ban />}
                onClick={onCancel}
                loading={pending === 'cancel'}
                disabled={busy && pending !== 'cancel'}
              >
                {jobQueued ? 'Leave the queue' : 'Cancel'}
              </Button>
            </Tooltip>
          ) : null}

          {!jobActive && failedOnPage > 0 ? (
            <Button
              size="sm"
              variant="secondary"
              iconLeft={<RotateCcw />}
              onClick={onRetryFailed}
              loading={pending === 'retry-failed'}
              disabled={busy}
            >
              Retry failed ({formatNumber(failedOnPage)})
            </Button>
          ) : null}

          <span aria-hidden="true" className="mx-1 hidden h-6 w-px bg-line sm:block" />

          <Button
            size="sm"
            variant="secondary"
            iconLeft={<SquareCheck />}
            onClick={selectedCount > 0 ? onClearSelection : onSelectAll}
            disabled={pageCount === 0 || busy}
          >
            {selectedCount > 0 ? `Deselect (${formatNumber(selectedCount)})` : 'Select all'}
          </Button>

          <Button
            size="sm"
            variant="success"
            iconLeft={<Check />}
            onClick={onApproveSelected}
            loading={pending === 'approve-selected'}
            disabled={selectedCount === 0 || busy}
          >
            Approve selected
            {selectedCount > 0 ? ` (${formatNumber(selectedCount)})` : ''}
          </Button>

          <Tooltip content={`Approve everything matching “${filterLabel}” that has alt text`}>
            <Button
              size="sm"
              variant="success"
              iconLeft={<CheckCheck />}
              onClick={onApproveAll}
              loading={pending === 'approve-all'}
              disabled={matchingTotal === 0 || busy}
            >
              Approve all
            </Button>
          </Tooltip>

          <Button
            size="sm"
            className="ml-auto"
            iconLeft={<CloudUpload />}
            onClick={onSave}
            loading={pending === 'save'}
            disabled={needsSaveCount === 0 || jobActive || busy}
          >
            Save to WordPress
            {needsSaveCount > 0 ? ` (${formatNumber(needsSaveCount)})` : ''}
          </Button>
        </div>
      </div>
    </>
  )
}
